"use client"

import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ArrowLeft, Droplet, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"

// not-found.tsx no recibe params: el idioma se deduce del primer segmento de la URL.
// Textos locales a propósito para no tocar lib/translations.ts.
const copy = {
  es: {
    seg: "es",
    eyebrow: "Página no encontrada",
    title: "Esta página se ha ido por el desagüe",
    subtitle:
      "No encontramos lo que buscas. Puede que el enlace esté roto o que la página se haya movido.",
    home: "Volver al inicio",
    contact: "Contactar",
  },
  gl: {
    seg: "gal",
    eyebrow: "Páxina non atopada",
    title: "Esta páxina marchou polo sumidoiro",
    subtitle:
      "Non atopamos o que buscas. Pode que a ligazón estea rota ou que a páxina se movese.",
    home: "Volver ao inicio",
    contact: "Contactar",
  },
  en: {
    seg: "en",
    eyebrow: "Page not found",
    title: "This page went down the drain",
    subtitle:
      "We couldn't find what you're looking for. The link may be broken or the page may have moved.",
    home: "Back to home",
    contact: "Contact us",
  },
} as const

function langFromPath(pathname: string | null) {
  const first = pathname?.split("/")[1]
  if (first === "en") return "en"
  if (first === "gal") return "gl"
  return "es"
}

export default function NotFoundPage() {
  const t = copy[langFromPath(usePathname())]
  const home = `/${t.seg}`

  return (
    <main className="relative min-h-[100dvh] overflow-hidden flex flex-col">
      {/* Mismo fondo que el hero de la landing, con el poster estático en vez del vídeo */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-100" />
      <Image
        src="/ocean-waves-image-compressed.webp"
        alt=""
        aria-hidden="true"
        fill
        priority
        sizes="100vw"
        className="object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/70 via-cyan-500/60 to-emerald-400/50" />
      <div className="absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none" />

      <header className="relative z-10 px-4 md:px-6 lg:px-16 xl:px-32 pt-[max(1.5rem,env(safe-area-inset-top))]">
        <Link href={home} className="inline-flex items-center group ml-1">
          <Image
            src="/display-blanco.webp"
            alt="EGS Logo"
            width={150}
            height={30}
            className="transition-transform group-hover:scale-110 h-[22px] md:h-[25px] w-auto"
            priority
          />
        </Link>
      </header>

      <section className="relative z-10 flex-1 flex items-center px-4 py-12 pb-[max(3rem,env(safe-area-inset-bottom))]">
        <div className="max-w-2xl mx-auto w-full text-center">
          {/* Gota con ondas: reutiliza las animaciones propias de globals.css */}
          <div className="relative mx-auto mb-6 md:mb-8 flex h-24 w-24 md:h-28 md:w-28 items-center justify-center">
            <span className="absolute inset-0 rounded-full border border-white/30 animate-pulse-subtle" />
            <span className="absolute -inset-4 md:-inset-6 rounded-full border border-white/20 animate-pulse-subtle-delay-1" />
            <span className="absolute -inset-8 md:-inset-12 rounded-full border border-white/10 animate-pulse-subtle-delay-2" />
            <div className="relative flex h-full w-full items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/40 shadow-lg animate-float">
              <Droplet className="h-10 w-10 md:h-12 md:w-12 text-white" strokeWidth={1.75} />
            </div>
          </div>

          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
            {t.eyebrow}
          </p>
          <h1 className="mt-2 text-7xl sm:text-8xl md:text-9xl font-bold leading-none text-white drop-shadow-sm">
            404
          </h1>
          <h2 className="mt-4 md:mt-6 text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight text-balance">
            {t.title}
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-white/90 max-w-xl mx-auto leading-relaxed text-pretty">
            {t.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto bg-white text-cyan-700 hover:bg-white/90 font-semibold px-8 shadow-lg"
            >
              <Link href={home}>
                <ArrowLeft />
                {t.home}
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto bg-white/15 text-white border-white/60 backdrop-blur-sm hover:bg-white/20 hover:text-white font-semibold px-8"
            >
              <Link href={`${home}#unete`}>
                <Mail />
                {t.contact}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  )
}
